import Header from "@/components/Header";
import Footer from "@/components/Footer";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto w-full px-6 lg:px-10 py-14">
        <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700">Privacy notice</p>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Privacy notice</h1>
        <p className="mt-2 text-sm text-slate-500">SmartNote2Self · Kaiventive Holdings Ltd</p>

        <div className="mt-8 space-y-8 text-[15px] leading-relaxed text-slate-700">
          <Section title="Who we are">
            <p>
              SmartNote2Self · Biz Resilience Scan is a product of Kaiventive Holdings Ltd ("we", "us").
              We provide AI-assisted operational resilience planning. For general enquiries, contact{" "}
              <a className="text-teal-700 underline underline-offset-2" href="mailto:marcelle@smartnote2self.com">
                marcelle@smartnote2self.com
              </a>
              . For privacy and data-rights requests, contact{" "}
              <a className="text-teal-700 underline underline-offset-2" href="mailto:marcelle@smartnote2self.com">
                marcelle@smartnote2self.com
              </a>
              .
            </p>
          </Section>

          <Section title="What we collect">
            <ul className="list-disc list-inside space-y-1.5">
              <li>Assessment inputs you type (operational answers).</li>
              <li>Optional details: organisation name, email address, uploaded logo.</li>
              <li>Account profile if you sign in with Google (email, name, profile picture).</li>
              <li>Anonymous product analytics (only if you accept analytics cookies).</li>
              <li>Email delivery audit logs (recipient, timestamp, message ID).</li>
            </ul>
          </Section>

          <Section title="Why we process it (lawful basis)">
            <ul className="list-disc list-inside space-y-1.5">
              <li><b>Service delivery</b> — to generate your continuity plan and PDF (contract / legitimate interest).</li>
              <li><b>Consent</b> — to send your plan by email, store your logo, and use analytics cookies.</li>
              <li><b>Legitimate interest</b> — to improve the product and prevent abuse.</li>
            </ul>
          </Section>

          <Section title="Sub-processors">
            <ul className="list-disc list-inside space-y-1.5">
              <li><b>Anthropic</b> — AI drafting of plan sections (Claude Sonnet 4.5 via Emergent's API proxy).</li>
              <li><b>Resend</b> — email delivery (plans, booking confirmations, consultant requests).</li>
              <li><b>Emergent</b> — application hosting, object storage, Google sign-in proxy.</li>
              <li><b>MongoDB</b> — database hosting.</li>
              <li><b>PostHog</b> — anonymous product analytics (only if you opt in).</li>
              <li><b>Meta Pixel</b> — advertising measurement (page views, scan starts, purchases) to gauge ad performance. Loaded only if you accept marketing/analytics cookies.</li>
            </ul>
            <p className="mt-3 text-[13px] leading-relaxed">
              <b>About AI training:</b> your assessment answers and any organisation context you provide
              are sent to Anthropic's Claude API only to generate your plan. Under Anthropic's commercial
              API policy, this data is <b>not used to train their models</b>. Anthropic may retain prompts
              and outputs for a short period (typically 30 days) strictly for abuse and safety monitoring,
              with access restricted to that purpose.
            </p>
          </Section>

          <Section title="Retention">
            <ul className="list-disc list-inside space-y-1.5">
              <li>Anonymous assessments: retained while in use; abandoned drafts (no activity for 90 days) are automatically purged.</li>
              <li>Completed plans: retained for 2 years from last activity, then automatically purged.</li>
              <li>Shareable links: automatically expire after 30 days.</li>
              <li>Email delivery logs: 12 months for support and audit.</li>
              <li>Signed-in account data: until you request deletion.</li>
            </ul>
          </Section>

          <Section title="Administrative access">
            <p>
              Authorised administrators may access aggregate statistics and individual assessment records
              to operate the service (e.g. support enquiries, abuse investigation, retention enforcement).
              By default, personally identifiable fields (email, organisation name) are shown in
              minimised form. Every administrative access to identifying or detailed answer data is recorded
              in an internal audit log to support our record-keeping obligations under applicable data-protection law.
            </p>
          </Section>

          <Section title="Your data rights">
            <p>
              Wherever you are located, you have meaningful rights over the personal data we hold
              about you. We honour the rights below — voluntarily where they are not legally required
              in your jurisdiction.
            </p>

            <p className="mt-3">
              <strong>For signed-in users</strong>, three of these rights are <strong>self-serve and instant</strong>{" "}
              on your <a className="text-teal-700 underline underline-offset-2" href="/account">Account page</a>{" "}
              under "Data &amp; privacy". For all other rights, or if you are not signed in (e.g. a visitor,
              prospect, or booking-only user), email{" "}
              <a className="text-teal-700 underline underline-offset-2" href="mailto:marcelle@smartnote2self.com">
                marcelle@smartnote2self.com
              </a>{" "}
              — we respond within 30 days.
            </p>

            <ul className="list-disc pl-5 mt-3 space-y-1.5">
              <li>
                <strong>Access</strong> — see and obtain a copy of every assessment input, booking, payment,
                share link, and account record we hold about you. <em>(Self-serve via Account page.)</em>
              </li>
              <li>
                <strong>Portability</strong> — "Download my data (JSON)" produces a complete machine-readable
                export of everything we hold about you. <em>(Self-serve.)</em>
              </li>
              <li>
                <strong>Erasure</strong> — "Delete my account" cascades through every collection
                (assessments, share links, delivery log, bookings, payments, sessions, profile) and cannot
                be undone. <em>(Self-serve.)</em>
              </li>
              <li>
                <strong>Rectification</strong> — correct any inaccurate or incomplete personal data we
                hold about you.
              </li>
              <li>
                <strong>Restriction of processing</strong> — ask us to pause processing while a dispute
                or objection is investigated.
              </li>
              <li>
                <strong>Objection</strong> — object to processing based on our legitimate interests.
              </li>
              <li>
                <strong>Withdraw consent</strong> — at any time, without affecting the lawfulness of prior
                consent-based processing (e.g. emailing your plan, storing your logo, analytics cookies).
              </li>
            </ul>

            <p className="mt-4 text-[14px]">
              <strong>Legal basis &amp; supervisory authorities.</strong> These rights are statutorily
              enforceable under the <strong>UK GDPR</strong> (with the UK Data Protection Act 2018), the{" "}
              <strong>EU GDPR</strong> (Regulation 2016/679), and Jamaica's{" "}
              <strong>Data Protection Act, 2020</strong>. You may lodge a complaint with the{" "}
              <a
                className="text-teal-700 underline underline-offset-2"
                href="https://ico.org.uk"
                target="_blank"
                rel="noopener noreferrer"
              >
                UK Information Commissioner's Office (ICO)
              </a>
              , your local{" "}
              <a
                className="text-teal-700 underline underline-offset-2"
                href="https://edpb.europa.eu/about-edpb/who-we-are/members_en"
                target="_blank"
                rel="noopener noreferrer"
              >
                EU supervisory authority
              </a>
              , or the{" "}
              <a
                className="text-teal-700 underline underline-offset-2"
                href="https://www.oic.gov.jm"
                target="_blank"
                rel="noopener noreferrer"
              >
                Office of the Information Commissioner of Jamaica (OIC)
              </a>
              .
            </p>

            <p className="mt-3 text-[14px]">
              <strong>A note on portability.</strong> Data portability (the right to a machine-readable
              export) is a statutory right under the UK and EU GDPR. Jamaica's Data Protection Act 2020
              grants the equivalent right of access in intelligible form but does not separately enumerate
              portability; we extend the same machine-readable JSON export to all users globally as a
              matter of policy.
            </p>
          </Section>

          <Section title="Cookies">
            <p>
              We use essential cookies needed for the app to work, and (with your consent) anonymous analytics
              cookies. You can change your choice at any time by clearing site data and reloading the app.
            </p>
          </Section>

          <Section title="No legal or audit advice">
            <p className="text-[13px] text-slate-500 italic">
              This service does not provide legal, regulatory, audit, or certification advice. You remain
              responsible for validating, implementing, testing, and maintaining your continuity arrangements.
            </p>
          </Section>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-xl font-semibold text-slate-900 tracking-tight">{title}</h2>
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  );
}
